<script setup lang="ts">
import { TrashIcon } from "@heroicons/vue/outline";
import { notify } from "@kyvg/vue3-notification";
import { useQuery } from "@vue/apollo-composable";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { type TmLibraryUploadsQuery, UserRole } from "@/__generated__/types";
import useDeleteRepositoryFile from "@/api/mutations/RepositoryFile/deleteRepositoryFile.mutation";
import useGenerateFileDownloadTokenMutation from "@/api/mutations/RepositoryFile/generateFileDownloadToken.mutation";
import AtButton from "@/components/atoms/AtButton/AtButton.vue";
import AtIconButton from "@/components/atoms/AtIconButton.vue";
import OgS3FilePicker from "@/components/molecules/MlFilePicker/OgS3FilePicker.vue";
import MlSelect from "@/components/molecules/MlSelect/MlSelect.vue";
import OgDataTable from "@/components/organisms/OgDataTable/OgDataTable.vue";
import AI_DOCUMENTS_QUERY from "@/components/pages/PgUserUploads/AiDocuments.query";
import dayjs from "@/lib/dayjs/config";

const { t } = useI18n();

const {
	result,
	loading: queryLoading,
	refetch,
} = useQuery<TmLibraryUploadsQuery>(
	AI_DOCUMENTS_QUERY,
	() => ({
		adminMode: false,
	}),
	{
		fetchPolicy: "network-only",
	},
);

const { mutate: generateFileDownloadURLMutation } =
	useGenerateFileDownloadTokenMutation();

const { mutate: deleteRepositoryFile } = useDeleteRepositoryFile();

const topics = computed<Record<string, string>>(
	() =>
		result.value?.getCategories
			.flatMap((c) =>
				c.subcategories.map((s) => [s.slug, `${t(c.slug)}: ${t(s.slug)}`]),
			)
			.reduce(
				(acc, curr) => {
					acc[curr[0]] = curr[1];

					return acc;
				},
				{} as Record<string, string>,
			) ?? {},
);

const projects = computed(
	() =>
		result.value?.entityLocationSummaries.reduce<Record<string, string>>(
			(acc, { _id, name }) => ({ ...acc, [_id]: name }),
			{},
		) ?? {},
);

const headers = computed(() => [
	{ text: t("Topics"), value: "relatedTopics" },
	{ text: t("Projects"), value: "relatedProjects" },
	{ text: t("Document"), value: "document" },
	{ text: t("Date"), value: "createdAt" },
	{ text: "", value: "crud" },
]);

const uploadTargetTopics = ref<string[]>([]);
const uploadTargetProjects = ref<string[]>([]);

const filteredAndSortedItems = computed(
	() =>
		result.value?.getEntityRepositoryFiles.map((file) => {
			return {
				relatedTopics:
					file.relatedTopicSlugs?.map((s) => t(s)).join(", ") ?? "",
				relatedProjects: file.relatedProjects?.map((l) => l.name),
				fileName: file.filename,
				createdAt: dayjs(file.createdAt).format("DD.MM.YYYY HH:mm"),
				_id: file._id,
			};
		}) ?? [],
);

const handleDownloadFile = async (repositoryFileId: string) => {
	const mutationResult = await generateFileDownloadURLMutation({
		repositoryFileId,
	});

	window.open(mutationResult?.data?.generateFileDownloadToken);
};

const handleDeleteFile = async (repositoryFileId: string) => {
	await deleteRepositoryFile({ repositoryFileId });
	await refetch();
};

const customFileMeta = computed(() => ({
	relatedTopicSlugs: uploadTargetTopics.value,
	relatedProjects: uploadTargetProjects.value,
}));

const onAfterUpload = () => {
	refetch();

	notify({
		type: "success",
		text: t("Your file has been uploaded."),
	});
};
</script>

<template>
  <div>
    <p class="text-sm text-gray-500 mt-3">
      {{ t('Here you can upload Reference Documents for AI Processing: These include materials such as codes of conduct (for suppliers, customers, or internal use), past sustainability reports, CDP/GRI disclosures, and similar documents. They are used by our AI to better understand your context and generate more accurate responses. These will be automatically generated on your workspace.') }}
    </p>


    <div class="grid grid-cols-3 gap-5 mt-24 mb-10">

      <div class="bg-blue-50 p-10">

        <p class="text-gray-400">{{t('The current upload should be used for the following:')}}</p>

        <MlSelect
            :options="topics"
            :sorted-options="true"
            :multiple="true"
            v-model="uploadTargetTopics"
            wrapperClass="mt-5"
            :placeholder="t('All topics')"
        />

        <MlSelect
            :options="projects"
            :sorted-options="true"
            :multiple="true"
            v-model="uploadTargetProjects"
            wrapperClass="mt-5"
            :placeholder="t('All projects')"
        />

      </div>

      <div class="bg-blue-50 p-10 text-gray-400 col-span-2">
        <OgS3FilePicker class="w-full" @update:modelValue="onAfterUpload" repository-file-type="ai_context" :custom-file-meta="customFileMeta" />
      </div>

    </div>


    <template v-if="!queryLoading">
      <OgDataTable
          :headers="headers"
          controlsHidden
          variant="default"
          :items="filteredAndSortedItems"
      >
        <template #item-document="row">
          <AtButton variant="link" data-cy="dataPointFile" @click="handleDownloadFile(row._id)">
            {{ row.fileName }}
          </AtButton>
        </template>
        <template #item-crud="row">
          <span
              class="grid grid-cols-1 place-items-center gap-x-2 justify-self-end"
          >
            <AtIconButton
                v-rolefrom="UserRole.ADMIN"
                :icon="TrashIcon"
                class="w-7 text-gray-400"
                :title="t('Delete')"
                @click.stop="handleDeleteFile(row._id)"
            />
          </span>
        </template>
      </OgDataTable>
    </template>



  </div>
</template>
